import { EmptyFeatureResult, SignalStoreFeature, signalStoreFeature, withHooks, withMethods, withProps } from '@ngrx/signals';
import { Subject } from 'rxjs';

type CancelSubjectKey<Name extends string> = `${Name}$`;
type CancelProps<Name extends string> = { [K in CancelSubjectKey<Name>]: Subject<void> };
type CancelMethods<Name extends string> = { [K in Name]: () => void };

/**
 * Reusable signal-store feature giving `rxMethod`s a "reset/cancel" primitive
 * equivalent to the classic NgRx `clearXData` action + `cancelOnAction` pattern.
 *
 * Given a method name (e.g. `'clear'`), this adds:
 * - a `${name}$` prop holding a `Subject<void>` (e.g. `clear$`)
 * - a `${name}()` method that emits on it (e.g. `clear()`)

 * The subject is completed on store destroy to avoid leaking subscriptions.
 */
export function withCancellation<Name extends string>(
  name: Name
): SignalStoreFeature<EmptyFeatureResult, { state: object; props: CancelProps<Name>; methods: CancelMethods<Name> }> {
  const subjectKey = `${name}$` as CancelSubjectKey<Name>;

  return signalStoreFeature(
    withProps(() => ({ [subjectKey]: new Subject<void>() }) as CancelProps<Name>),
    withMethods((store) => {
      const subject = (store as unknown as CancelProps<Name>)[subjectKey];

      return { [name]: () => subject.next() } as CancelMethods<Name>;
    }),
    withHooks({
      onDestroy(store) {
        (store as unknown as CancelProps<Name>)[subjectKey].complete();
      },
    })
  );
}
